import * as React from 'react';
import * as I18n from 'i18n-js';
import PostalAddress from 'i18n-postal-address';
import { ChildProps } from 'react-apollo';
import { decamelizeKeys } from 'humps';

import {
  core_apimessages_Address as IAddress,
  core_apimessages_Country as ICountry,
  Countries,
} from '../../schemas/rql';
import withCountriesApollo from './with_countries_apollo';
import AddressFieldsConfig from './address_fields_config';

import experiments from '@reverbdotcom/commons/dist/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/dist/user_hooks';

interface IExternalProps {
  address: IAddress;
}

type IProps = ChildProps<IExternalProps, Countries.Query>;

const findCountry = (countryCode: string, countries: ICountry[]): ICountry => {
  return countries.find(c => c.countryCode === countryCode);
};

const countryCodeToName = (countryCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  return country && country.name;
};

const regionNameInCountry = (countryCode: string, regionCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  const region = country && country.subregions.find(r => (r.code === regionCode));
  return region && region.name;
};

function isIncomplete(address: IAddress, intlAddressForm) {
  if (intlAddressForm) {
    const fields = AddressFieldsConfig[address.countryCode] || AddressFieldsConfig.default;
    const decamelizedAddress = decamelizeKeys(address);

    return fields.some(field => !decamelizedAddress[field.fieldName] && field.required);
  }

  return (!address.streetAddress
    || !address.locality
    || !address.region
    || !address.postalCode);
}

function getPostalAddress(address: IAddress, regionName) {
  const postalAddress = new PostalAddress();
  return postalAddress
    .setFirstName(address.name)
    .setAddress1(address.streetAddress)
    .setAddress2(address.extendedAddress)
    .setCity(address.locality)
    .setState(regionName)
    .setProvince(regionName)
    .setPostalCode(address.postalCode)
    .setFormat({
      country: address.countryCode,
    })
    .output();
}

function displayAddress(line, idx) {
  if (idx === 1) {
    return (
      <>
        <span className="formatted-address__highlight">
          {line}
        </span>
        <br />
      </>
    );
  }

  if (Array.isArray(line)) {
    return (
      <>
        <span>{line.join(' ')}</span>
        <br />
      </>
    );
  }

  return (
    <>
      <span>{line}</span>
      <br />
    </>
  );
}

function flattenPostalAddressLines(address) {
  return address.map(line => {
    if (line.length > 1) {
      return line;
    }

    return line[0];
  });
}

function AddressSummary({ address, data }: IProps) {
  const { countries } = data.countries;
  const countryName = countryCodeToName(address.countryCode, countries);
  const regionName = regionNameInCountry(address.countryCode, address.region, countries);
  const intlAddressForm = useExpEnabled(experiments.INTERNATIONAL_ADDRESS_FORM_CHECKOUT);
  const postalAddressDisplay = flattenPostalAddressLines(getPostalAddress(address, regionName));

  const {
    name,
    streetAddress,
    locality,
    extendedAddress,
  } = address;

  return (
    <div className="formatted-address">
      <p className="formatted-address__info">
        {intlAddressForm ?
          postalAddressDisplay.map(displayAddress) :
          <>
            {name &&
              <>
                {name}
                <br />
              </>
            }
            {streetAddress &&
              <>
                <span className="formatted-address__highlight">
                  {streetAddress}
                </span>
                <br />
              </>
            }
            {extendedAddress &&
              <>
                {extendedAddress}
                <br />
              </>
            }
            {locality && `${locality},`} {regionName} {address.postalCode}
            <br />
          </>
        }
        {countryName}
      </p>
      {isIncomplete(address, intlAddressForm) &&
        <p className="formatted-address__incomplete">
          {I18n.t('discovery.addressBook.addressSummary.incompleteAddress')}
        </p>
      }
    </div>
  );
}

const AddressSummaryWithCountries = withCountriesApollo<IExternalProps>(AddressSummary);
export { AddressSummary, AddressSummaryWithCountries };



// WEBPACK FOOTER //
// ./node_modules/@reverbdotcom/discovery-ui/src/components/addresses/address_summary.tsx